import { useCallback, useEffect, useState } from 'react';

/**
 * Light/dark on a brand's public site.
 *
 * The visitor's choice is remembered per brand domain, because a customer who
 * prefers dark on one shop has said nothing about another. 'system' follows
 * the device, which is what someone who has never chosen actually wants —
 * their phone is already set the way they like it.
 *
 * Dark mode is applied by inverting the section colours at render time rather
 * than asking brands to pick two palettes. Most never would, and a toggle
 * that only works for the brands who did is worse than no toggle: the
 * visitor flips it, nothing happens, and the site looks broken.
 */
const KEY = 'tpo_site_theme';

const systemPrefersDark = () => (
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-color-scheme: dark)').matches
);

const read = (defaultMode) => {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (_) { /* private mode */ }
  return defaultMode || 'system';
};

export default function useSiteTheme(defaultMode = 'system') {
  const [mode, setMode] = useState(() => read(defaultMode));

  // Follow the device while the choice is 'system' — someone switching their
  // phone to dark at sunset should see this site follow, without a reload.
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setMode((m) => (m === 'system' ? 'system' : m));
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [mode]);

  const isDark = mode === 'dark' || (mode === 'system' && systemPrefersDark());

  const toggle = useCallback(() => {
    setMode(() => {
      const next = isDark ? 'light' : 'dark';
      try { localStorage.setItem(KEY, next); } catch (_) { /* private mode */ }
      return next;
    });
  }, [isDark]);

  return { isDark, mode, toggle };
}

/**
 * How light a colour reads, 0 to 1, or null if it is not a hex colour.
 *
 * Rec. 601 weighting — close enough to perceived brightness for deciding
 * whether something is "a light ground".
 */
export const luminance = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};

/**
 * The header and footer for the current theme.
 *
 * These were the one part of the site that ignored the switch entirely. The
 * page behind them turned dark and the bar kept the colours the owner picked
 * for a white page — so on a dark site the menu was near-black text on a
 * near-black bar, and the theme toggle, which is drawn in that same ink, was
 * invisible rather than missing.
 *
 * Two ways to get the dark colours, in order:
 *
 *   1. What the owner chose. `part.dark.backgroundColor` / `.textColor`, set
 *      in Global design. Nobody has to fill these in, but a brand whose bar
 *      carries its identity should be able to say what it looks like at
 *      night rather than accept what arithmetic picked.
 *   2. Derived from the light colours, the same way sections are: a light
 *      bar becomes dark, an already-dark bar is left alone, and the ink
 *      flips to match whichever ground it ends up on.
 *
 * Each colour falls back on its own, so setting only the background is
 * allowed and the text still comes out readable.
 */
export const themedFrame = (part, isDark) => {
  const bg = part?.backgroundColor;
  const fg = part?.textColor;
  if (!isDark || !part) return { bg, fg };

  const chosen = part.dark || {};
  const lum = luminance(bg);
  /**
   * Derived the same way a section is, so a warm page does not get a grey bar
   * sitting on top of warm bands. The frame and the page have to agree, or
   * the site reads as two sites.
   */
  const derived = (lum === null || lum < 0.5)
    ? { bg, fg: (luminance(fg) !== null && luminance(fg) < 0.5 ? '#e5e7eb' : (fg || '#e5e7eb')) }
    : themedSection({ backgroundColor: bg, textColor: fg }, true);
  const derivedBg = derived.bg;
  const derivedFg = derived.fg;

  return {
    bg: String(chosen.backgroundColor || '').trim() || derivedBg,
    fg: String(chosen.textColor || '').trim() || derivedFg,
  };
};

/** A hex colour as hue (0-360), saturation and lightness (0-1). */
export function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

/** The reverse. */
export function hslToHex({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
      : h < 180 ? [0, c, x]
        : h < 240 ? [0, x, c]
          : h < 300 ? [x, 0, c]
            : [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/**
 * A section's colours for the current theme.
 *
 * Brands pick one palette, almost always a light one. Rather than demanding a
 * second, dark mode derives from the first — but it derives properly now,
 * which the first version did not.
 *
 * That version mapped every light ground to one of two fixed greys. It worked,
 * in that the text could be read, and it was wrong in a way that is obvious
 * once seen: it threw the brand away. A warm bakery in sand, a forest-green
 * charity and a cold slate agency all turned into the same neutral charcoal,
 * so every site on the platform looked identical in dark mode. The one thing a
 * brand's own site must not do is look like everybody else's.
 *
 * So the hue survives. A warm cream ground becomes a warm near-black; a green
 * tint becomes a green-tinted near-black. Saturation is pulled down, because a
 * saturated dark reads as muddy rather than as colour, but it is not removed.
 *
 * Lightness is INVERTED rather than flattened, which is what keeps a page
 * legible: the whitest band becomes the darkest, the tinted band stays a step
 * lighter, and a page built on alternating bands still alternates. Two fixed
 * greys could only ever express two steps, so a third tint was lost.
 */
const DARKEST = 0.065;
const LIGHTEST = 0.145;

export const themedSection = (section, isDark) => {
  if (!isDark) return { bg: section.backgroundColor, fg: section.textColor };

  const bg = String(section.backgroundColor || '').trim();
  const fg = String(section.textColor || '').trim();
  const hsl = hexToHsl(bg);

  // Transparent, a gradient, or not a colour we can read: leave the ground
  // alone and only make sure the words on it can be seen.
  if (!hsl) return { bg: section.backgroundColor, fg: fg || '#e5e7eb' };

  // Already dark — a hero the brand deliberately made navy. Keep it: the owner
  // chose that, and it already works on a dark page.
  if (hsl.l < 0.5) return { bg: section.backgroundColor, fg: fg || '#e5e7eb' };

  /**
   * Map [0.5 … 1] onto [LIGHTEST … DARKEST], inverted.
   *
   * Inverted because the page's rhythm is what carries it: on a light page the
   * white band is the "clean" one and the tint is the "set apart" one, and
   * that relationship should survive, not reverse. White is the furthest from
   * dark, so it becomes the darkest; the tint sits a step up from it.
   */
  const t = Math.min(1, Math.max(0, (hsl.l - 0.5) / 0.5));
  const l = LIGHTEST - t * (LIGHTEST - DARKEST);

  return {
    bg: hslToHex({ h: hsl.h, s: Math.min(hsl.s, 0.18), l }),
    // Ink of the same hue rather than a flat grey, so a warm page stays warm
    // all the way down to the text.
    fg: hslToHex({ h: hsl.h, s: Math.min(hsl.s, 0.12), l: 0.90 }),
  };
};
